import * as $ from "jquery";
import * as _ from "lodash";
import * as Backbone from "backbone";
import { ActionPacketCollection } from "../../collections/ActionPackets";
import { BacnetConfigView } from "./sensor_config/BacnetConfigView";
import { BaseModuleConfigView, BaseSensorConfigView } from "./sensor_config/_BaseSensorConfigView";
import { BaseSettingsView } from "./_BaseSettingsView";
import { ChannelParamCollection } from "../../collections/ChannelParams";
import { ConversionFunctionCollection } from "../../collections/ConversionFunctions";
import { GenericConfigView, LayoutConfig } from "./sensor_config/GenericConfigView";
import { Intercom } from "../../common/intercom";
import { ModbusMasterConfigView } from "./sensor_config/ModbusMasterConfigView";
import { Module } from "../../models/Module";
import { ModuleCollection } from "../../collections/Modules";
import { ModuleConfigView } from "./sensor_config/ModuleConfigView";
import { ModuleV5ConfigView } from "./sensor_config/ModuleV5ConfigView";
import { NoConfigView } from "./sensor_config/NoConfigView";
import { PermissionCollection } from "../../collections/Permissions";
import { SensorConfigCollection } from "../../collections/SensorConfigs";
import { User } from "../../models/User";

require('bootstrap-toggle');
var sensorConfigTemplate = require('templates/settings/sensorconfig.html');

export interface GetConfigViewOptions {
    permissionCollection: PermissionCollection,
    conversionFunctionCollection: ConversionFunctionCollection,
    channelParamCollection: ChannelParamCollection,
    sensorConfigCollection: SensorConfigCollection,
    module: Module,
    connector: number,
    installId: number,
    orgId?: number,
    genericConfigCanDeleteChannel?: boolean,
    currentUser: User,
    organizationCollection?: any,
    installCollection?: any
}

export class SensorConfigViewFactory {

    private getConfig(options: GetConfigViewOptions) {
        var con = options.module.getConnector(options.connector);
        return options.sensorConfigCollection.where({sensor_id: con['sensor_id']});
    }

    private getGenericConfigViewInstance(options: GetConfigViewOptions, layoutConfig?: LayoutConfig) {
        var config = this.getConfig(options);
        if (!config) config = [];

        var con = options.module.getConnector(options.connector);
        if (!layoutConfig) layoutConfig = {
            sensorName: con['sensor_name'],
            fields: []
        };

        const viewOptions: any = {
            config: config,
            module: options.module,
            con: con,
            permissionCollection: options.permissionCollection,
            conversionFunctionCollection: options.conversionFunctionCollection,
            channelParamCollection: options.channelParamCollection,
            installId: options.installId,
            sensorId: con['sensor_id'],
            orgId: options.orgId,
            layoutConfig: layoutConfig,
            canDeleteChannel: options.genericConfigCanDeleteChannel,
            currentUser: options.currentUser
        };

        // Pass organizationCollection and installCollection if available
        if (options.organizationCollection) {
            viewOptions.organizationCollection = options.organizationCollection;
        }
        if (options.installCollection) {
            viewOptions.installCollection = options.installCollection;
        }

        return new GenericConfigView(viewOptions);
    }

    private getConfigViewInstance(klass: typeof BaseSensorConfigView, options: GetConfigViewOptions) {
        var con = options.module.getConnector(options.connector);

        const viewOptions: any = {
            config: con ? this.getConfig(options): [],
            module: options.module,
            con: con,
            permissionCollection: options.permissionCollection,
            conversionFunctionCollection: options.conversionFunctionCollection,
            channelParamCollection: options.channelParamCollection,
            installId: options.installId,
            sensorId: con ? con['sensor_id'] : null,
            orgId: options.orgId,
            currentUser: options.currentUser
        };

        // Pass organizationCollection and installCollection if available
        if ((options as any).organizationCollection) {
            viewOptions.organizationCollection = (options as any).organizationCollection;
        }
        if ((options as any).installCollection) {
            viewOptions.installCollection = (options as any).installCollection;
        }

        return new klass(viewOptions);
    }

    getConfigView(options: GetConfigViewOptions) {
        var con = options.module.getConnector(options.connector);

        if (!con) {
            // Virtual modules do not have a internal sensor
            if (options.connector === 0) {
                return this.getConfigViewInstance(BaseModuleConfigView, options);
            }
            return null
        }

        var view;
        var sensorName = con['sensor_name'];
        var sensorVersion = con['sensor_version'];
        var sensorType = con['sensor_type'];
        var sensorFirmwareVersion = con['sensor_firmware_version'];
        var config = this.getConfig(options);

        if (config.length) {
            switch (sensorType) {
                case 0: // Internal
                    if (sensorFirmwareVersion < 300) {
                        view = this.getConfigViewInstance(BaseModuleConfigView, options);
                    } else if (sensorVersion < 3) {
                        view = this.getConfigViewInstance(ModuleConfigView, options);
                    } else{
                        view = this.getConfigViewInstance(ModuleV5ConfigView, options);
                    }
                    break;
                case 12: // Air Quality
                case 17: // Temperature & Humidity
                    if ((sensorVersion === 14 || sensorVersion === 15) && sensorFirmwareVersion >= 300) {
                        view = this.getGenericConfigViewInstance(options, {
                            sensorName: sensorName,
                            fields: [{
                                type: 'hidden',
                                configNames: ['RESOLUTION']
                            }]
                        });
                    }
                    break;
                case 18: // Contact Closure
                    if (sensorVersion === 17) {
                        view = this.getGenericConfigViewInstance(options, {
                            sensorName: sensorName,
                            fields: [{
                                type: 'toggle',
                                label: 'LEDs',
                                configNames: ['LEDS_OFF'],
                                inverted: true
                            }, {
                                type: 'toggle',
                                label: 'State Change Notification',
                                configNames: ['POLL_OFF'],
                                inverted: true
                            }]
                        });
                    } else if (sensorVersion === 18) {
                        view = this.getGenericConfigViewInstance(options, {
                            sensorName: sensorName,
                            fields: [{
                                type: 'toggle',
                                label: 'LEDs',
                                configNames: ['LEDS_OFF'],
                                inverted: true
                            }, {
                                type: 'toggle',
                                label: 'State Change Notification',
                                configNames: ['POLL_OFF'],
                                inverted: true
                            }, {
                                type: 'dropdown',
                                label: 'Mode',
                                options: [{
                                    value: 0,
                                    label: 'Bridge Voltage Source'
                                }, {
                                    value: 2,
                                    label: 'External Voltage Source'
                                }, {
                                    value: 1,
                                    label: 'Contact to ground'
                                }],
                                configNames: ['MODE']
                            }]
                        });
                    }
                    break;
                case 21: // Pulse
                    if (sensorVersion === 2) {
                        view = this.getGenericConfigViewInstance(options, {
                            sensorName: sensorName,
                            fields: [{
                                type: 'toggle',
                                label: 'LEDs',
                                configNames: ['LEDS_OFF'],
                                inverted: true
                            }]
                        });
                    } else if (sensorVersion === 3) {
                        view = this.getGenericConfigViewInstance(options, {
                            sensorName: sensorName,
                            fields: [{
                                type: 'toggle',
                                label: 'LEDs',
                                configNames: ['LEDS_OFF'],
                                inverted: true
                            }, {
                                type: 'dropdown',
                                label: 'Mode',
                                options: [{
                                    value: 0,
                                    label: 'Bridge Voltage Source'
                                }, {
                                    value: 2,
                                    label: 'External Voltage Source'
                                }, {
                                    value: 1,
                                    label: 'Pulse to ground'
                                }],
                                configNames: ['MODE']
                            }]
                        });
                    }
                    break;
                case 22: // Modbus
                    view = this.getConfigViewInstance(ModbusMasterConfigView, options);
                    break;
                case 14: // Light Exposure
                case 24: // 4-20mA
                case 25: // Thermistor
                case 26: // Volt
                case 37: // Dual Volt/Thermistor
                    var filteredSamplingOptions = _.filter([{
                        value: 0,
                        label: 'Use sampling frequency (recommended when battery powered)'
                    }, {
                        value: 10,
                        label: '10 milliseconds'
                    }, {
                        value: 100,
                        label: '100 milliseconds'
                    }, {
                        value: 1000,
                        label: '1 second'
                    }, {
                        value: 60000,
                        label: '60 seconds'
                    }, {
                        value: 300000,
                        label: '5 minutes'
                    }], (option)=>{
                        return option.value < options.module.getPollingFrequency()*1000;
                    });
                    view = this.getGenericConfigViewInstance(options, {
                        sensorName: sensorName,
                        fields: [{
                            type: 'dropdown',
                            label: 'Subsampling Frequency',
                            options: filteredSamplingOptions,
                            configNames: ['SAMPLING_L', 'SAMPLING_H']
                        }]
                    });
                    break;
                case 27: // Bacnet
                    view = this.getConfigViewInstance(BacnetConfigView, options);
                    break;
                case 28: // Motion
                    view = this.getGenericConfigViewInstance(options, {
                        sensorName: sensorName,
                        fields: [{
                            type: 'toggle',
                            label: 'State Change Notification',
                            configNames: ['POLL_OFF'],
                            inverted: true
                        }]
                    });
                    break;
                case 29: // Timer Control
                    view = this.getConfigViewInstance(NoConfigView, options);
                    break;
                case 33: // Water Encoder
                    view = this.getGenericConfigViewInstance(options, {
                        sensorName: sensorName,
                        fields: [{
                            type: 'dropdown',
                            label: 'Encoder Port 1',
                            options: [{
                                value: 0,
                                label: 'Read Meter'
                            }, {
                                value: 1,
                                label: 'Read Meter Meta Data'
                            }],
                            configNames: ['READING_MODE_1']
                        }, {
                            type: 'dropdown',
                            label: 'Encoder Port 2',
                            options: [{
                                value: 0,
                                label: 'Read Meter'
                            }, {
                                value: 1,
                                label: 'Read Meter Meta Data'
                            }],
                            configNames: ['READING_MODE_2']
                        }]
                    });
                    break;
                case 34: // Indoor Air Quality
                    var filteredSamplingOptions = _.filter([{
                        value: 0,
                        label: 'Use sampling frequency (recommended when battery powered)'
                    }, {
                        value: 1000,
                        label: '1 second'
                    }, {
                        value: 60000,
                        label: '60 seconds'
                    }, {
                        value: 300000,
                        label: '5 minutes'
                    }], (option)=>{
                        return option.value < options.module.getPollingFrequency()*1000;
                    });

                    // Only show temperature offset fields if the sensor version >= 3 &&
                    // there is a Temperature (position = 1) channel present
                    const tempChannel = con['channels'].find(c => c.channel_position === 1);
                    if (sensorVersion >= 3 && tempChannel) {
                        view = this.getGenericConfigViewInstance(options, {
                            sensorName: sensorName,
                            fields: [{
                                type: 'dropdown',
                                label: 'Subsampling Frequency',
                                options: filteredSamplingOptions,
                                configNames: ['SAMPLING_L', 'SAMPLING_H']
                            }, {
                                type: 'tempOffset',
                                label: 'Temperature Offset',
                                configNames: ['TEMP_OFFSET']
                            }, {
                                type: 'tempOffset_dropdown',
                                label: 'Unit',
                                configNames: ['TEMP_OFFSET_UNIT']
                            }]
                        });
                    } else {
                        view = this.getGenericConfigViewInstance(options, {
                            sensorName: sensorName,
                            fields: [{
                                type: 'dropdown',
                                label: 'Subsampling Frequency',
                                options: filteredSamplingOptions,
                                configNames: ['SAMPLING_L', 'SAMPLING_H']
                            }]
                        });
                    }
                    break;
                case 35: // Indoor Environmental Quality
                    var filteredSamplingOptions = _.filter([{
                        value: 0,
                        label: 'Use sampling frequency (recommended when battery powered)'
                    }, {
                        value: 100,
                        label: '100 milliseconds'
                    }, {
                        value: 1000,
                        label: '1 second'
                    }, {
                        value: 60000,
                        label: '60 seconds'
                    }, {
                        value: 300000,
                        label: '5 minutes'
                    }], (option)=>{
                        return option.value < options.module.getPollingFrequency()*1000;
                    });
                    view = this.getGenericConfigViewInstance(options, {
                        sensorName: sensorName,
                        fields: [{
                            type: 'dropdown',
                            label: 'Subsampling Frequency',
                            options: filteredSamplingOptions,
                            configNames: ['SAMPLING_L', 'SAMPLING_H'],
                            ifAnyPositionsPresent: [101].concat(_.range(1, 12))
                        }, {
                            type: 'dropdown',
                            label: 'Vibration Sensitivity',
                            options: [{
                                value: 50,
                                label: 'Extreme (0.5° or 0.5 m/s²)'
                            }, {
                                value: 100,
                                label: 'High (1° or 1 m/s²)'
                            }, {
                                value: 300,
                                label: 'Medium (3° or 3 m/s²)'
                            }, {
                                value: 500,
                                label: 'Low (5° or 5 m/s²)'
                            }, {
                                value: 1000,
                                label: 'Minimal (10° or 10 m/s²)'
                            }],
                            configNames: ['VIBRATION_SENSITIVITY'],
                            ifAnyPositionsPresent: _.range(87, 101)
                        }, {
                            type: 'toggle',
                            label: 'State Change Notification',
                            configNames: ['POLL_OFF'],
                            inverted: true,
                            ifAnyPositionsPresent: [101]
                        }]
                    });
                    break;
                case 38: // Dual Pulse/Contact Closure
                    view = this.getGenericConfigViewInstance(options, {
                        sensorName: sensorName,
                        fields: [{
                            type: 'toggle',
                            label: 'LEDs',
                            configNames: ['LEDS_OFF'],
                            inverted: true
                        }, {
                            type: 'toggle',
                            label: 'State Change Notification',
                            configNames: ['POLL_OFF'],
                            inverted: true
                        }, {
                            type: 'dropdown',
                            label: 'Mode',
                            options: [{
                                value: 0,
                                label: 'Bridge Voltage Source'
                            }, {
                                value: 2,
                                label: 'External Voltage Source'
                            }, {
                                value: 1,
                                label: 'Pulse/Contact to ground'
                            }],
                            configNames: ['MODE']
                        }]
                    });
                    break;
            }
        }

        if (!view) {
            view = this.getGenericConfigViewInstance(options);
        }

        return view;
    }
}


export class SensorConfigView extends BaseSettingsView {
    private template = _.template(sensorConfigTemplate);

    private moduleCollection: ModuleCollection;
    private sensorConfigCollection: SensorConfigCollection = null;
    private conversionFunctionCollection: ConversionFunctionCollection = null;
    private channelParamCollection: ChannelParamCollection = null;

    private sensorConfigViewFactory = new SensorConfigViewFactory();
    private currentConfigViews: BaseSensorConfigView[];
    private moduleConfigView: BaseModuleConfigView;
    private install: Backbone.Model;
    private module: Module;

    private actionPackets: ActionPacketCollection;

    private collectionLoaded = false;
    private saved = false;

    constructor(options) {
        super(<any> _.extend(options, {
            el: '#settings-main-subview',
            events: {
                'click #settings-sensorconfig-save-button': 'handleSaveButtonClick',
            }
        }));

        this.moduleCollection = new ModuleCollection([], {installId: this.options.installId});
        this.install = this.options.installCollection.getOrgInstall(this.options.installId, this.options.orgId);
        this.actionPackets = new ActionPacketCollection([], {installId: this.options.installId});
    }

    show() {
        this.collectionLoaded = false;
        this.currentConfigViews = [];
        this.moduleConfigView = null;
        this.render();

        $.when(this.moduleCollection.fetch({reset: true})).done(() => {
            if (!this.options.moduleId) {
                var module = this.moduleCollection.first();
                if (module) this.options.moduleId = module.get('module_id');
            }
            if (!this.options.moduleId) return;

            this.options.moduleId = Number(this.options.moduleId);
            this.module = this.moduleCollection.findWhere({module_id: this.options.moduleId});
            this.sensorConfigCollection = new SensorConfigCollection([], {moduleId: this.options.moduleId});
            this.conversionFunctionCollection = new ConversionFunctionCollection([], {moduleId: this.options.moduleId});
            this.channelParamCollection = new ChannelParamCollection([], {moduleId: this.options.moduleId});

            $.when(
                this.sensorConfigCollection.fetch({reset: true}),
                this.conversionFunctionCollection.fetch({reset: true}),
                this.channelParamCollection.fetch({reset: true})
            ).done(() => {
                this.collectionLoaded = true;
                this.render();
            })
        });
    }

    render() {
        this.$el.html(this.template({
            currentOrgId: this.options.orgId,
            currentInstallId: this.options.installId,
            currentModuleId: this.options.moduleId,
            module: this.module,
            install: this.install,
            collectionLoaded: this.collectionLoaded,
            moduleCollection: this.moduleCollection,
            permissionCollection: this.options.permissionCollection,
            installId: this.options.installId,
            saved: this.saved
        }));

        this.renderSubViews();

        // Make checkboxes a toggle switch
        (<any>this.$('[data-toggle="toggle"]')).bootstrapToggle();

        return this;
    };

    renderSubViews() {
        if (!this.collectionLoaded) return;

        var configOptions = {
            permissionCollection: this.options.permissionCollection,
            conversionFunctionCollection: this.conversionFunctionCollection,
            channelParamCollection: this.channelParamCollection,
            sensorConfigCollection: this.sensorConfigCollection,
            module: this.module,
            installId: this.options.installId,
            currentUser: this.options.currentUser,
            orgId: this.options.orgId,
            organizationCollection: this.options.organizationCollection,
            installCollection: this.options.installCollection
        };

        this.moduleConfigView = this.sensorConfigViewFactory.getConfigView(_.extend({
            connector: 0
        }, configOptions));
        this.currentConfigViews.push(this.moduleConfigView);
        this.$('.sensorconfig-views-container').append(this.moduleConfigView.render().el);

        _.each([1, 2, 3, 4], (i) => {
            var view = this.sensorConfigViewFactory.getConfigView(_.extend({
                connector: i
            }, configOptions));

            if(view) {
                this.$('.sensorconfig-views-container').append(view.render().el);
                this.currentConfigViews.push(view);
            }
        });
    }


    async getUpdatedConfig() {
        const configs = [];
        for (const view of this.currentConfigViews) {
            if (!view.hasUpdatedConfig()) {
                continue;
            }
            const cfg = await view.getConfig();
            configs.push(cfg);
        }

        return _.flatten(configs);
    }

    async handleSaveButtonClick() {
        Intercom.trackEvent('changed-sensor-config', {
            install_id: this.options.installId,
            module_id: this.options.moduleId
        });
        var view = this;

        // Tell subviews to prepare for saving if they have to
        _.each(this.currentConfigViews, (view) => {
            if (view.prepareForSaving) view.prepareForSaving();
        });

        if (_.some(_.map(this.currentConfigViews, function (view) {
            return !view.validate()
        }))) {
            // Scroll to error
            $('html, body').animate({
                scrollTop: ($(".alert-danger:not(.hidden)").offset().top - 200)
            }, 100);
            return
        }

        let updatedConfig;
        try {
            updatedConfig = await this.getUpdatedConfig();
        } catch (error) {
            let msg = 'Please reload the page and try again';
            if (error == 'TEMP_OFFSET_VALUE') msg = 'Temperature offset value must be between -10 and 10.';
            (<any>$).notify({
                title: 'Something went wrong',
                message: msg,
                icon: 'fa fa-exclamation-circle',
            }, {
                animate: {
                    enter: 'animated fadeInUp',
                },
                // The type property defines the css style
                // See alerts.less for available styles
                type: "warning-alt",
                placement: { from: "bottom", align: "center" }
            });
            return;
        }

        var updatedChannels = { create: [], update: {}, 'delete': [] };
        _.each(this.currentConfigViews, function (view) {
            var viewChannels = view.getUpdatedChannels();
            updatedChannels['update'] = _.extend(updatedChannels['update'], viewChannels['update']);
            _.each(['create', 'delete'], function (op) {
                updatedChannels[op] = updatedChannels[op].concat(viewChannels[op]);
            });
        });

        var updatedParams = {create: [], update: {}, 'delete': []};
        _.each(this.currentConfigViews, function (view) {
            var viewParams = view.getUpdatedChannelParams();
            _.each(['create', 'update', 'delete'], function (op) {
                updatedParams[op] = _.extend(updatedParams[op], viewParams[op]);
            });
        });

        var requestData = {
            config: updatedConfig,
            channels: updatedChannels,
            channelParams: updatedParams
        };

        var moduleLocation = this.module.get('module_location');
        if (this.moduleConfigView.getModuleLocation() !== moduleLocation) {
            requestData['moduleLocation'] = this.moduleConfigView.getModuleLocation();
        }

        // Handle module outdoors value for IAQ sensors
        var moduleOutdoorsValue = this.moduleConfigView.getModuleOutdoorsValue();
        if (moduleOutdoorsValue !== undefined) {
            requestData['moduleOutdoorsValue'] = moduleOutdoorsValue;
        }

        var actionPackets: any[][] = [];
        _.each(this.currentConfigViews, function (view) {
            var packets = view.getActionPackets();
            if (_.size(packets)) actionPackets.push(packets);
        });

        var actionPacketDeferreds = _.map(_.flatten(actionPackets), (actionPacket) => {
            return $.ajax({
                type: "POST",
                url: this.actionPackets.url(),
                data: JSON.stringify(actionPacket),
                dataType: 'json',
                contentType: "application/json; charset=utf-8"
            });
        });

        this.collectionLoaded = false;
        this.render();

        return new Promise((resolve, reject) => {
            $.when($.ajax({
                url: this.sensorConfigCollection.url(),
                type: 'PUT',
                data: JSON.stringify(requestData),
                dataType: 'json',
                contentType: "application/json; charset=utf-8"
            })).fail((err) => {
                reject(err);
            }).then(() => {
                $.when.apply($, actionPacketDeferreds).done(() => {
                    this.show();
                    resolve(null);
                });
            });
        });

    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/settings/SensorConfigView.ts